import { useState, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Building2, Car, Gem, Users, Shield, TrendingUp, Globe, ChevronRight, Check, Sparkles, Phone, Mail, Briefcase } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import PartnerCountrySwitch from '@/components/PartnerCountrySwitch';
import axios from 'axios';
import { toast } from 'sonner';
import { Logo } from '@/components/Logo';
import { RegionSelector } from '@/components/RegionSelector';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERO_BG = 'https://images.unsplash.com/photo-1772175206880-cac7fbc8377f?w=1920&q=80';
const serif = { fontFamily: "'Playfair Display', serif" };
const gold = '#C6A15B';

const COUNTRY_CITIES = {
  'UAE': ['Dubai', 'Abu Dhabi', 'Sharjah', 'Ajman', 'Ras Al Khaimah', 'Fujairah'],
  'India': ['Mumbai', 'Delhi', 'Bangalore', 'Hyderabad', 'Pune', 'Chennai', 'Kolkata', 'Ahmedabad', 'Goa', 'Gurugram', 'Noida'],
  'United Kingdom': ['London', 'Manchester', 'Birmingham', 'Edinburgh', 'Bristol', 'Leeds'],
  'Saudi Arabia': ['Riyadh', 'Jeddah', 'Dammam', 'NEOM', 'Mecca'],
  'Singapore': ['Singapore'],
  'USA': ['New York', 'Los Angeles', 'Miami', 'San Francisco', 'Chicago'],
  'Other': [],
};

const Fade = ({ children, delay = 0, className = '' }) => (
  <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-60px' }} transition={{ duration: 0.7, delay }} className={className}>
    {children}
  </motion.div>
);

const BUSINESS_TYPES = [
  { value: 'developer', label: 'Real Estate Developer' },
  { value: 'broker', label: 'Brokerage / Agency' },
  { value: 'agent', label: 'Individual Agent' },
  { value: 'car_dealer', label: 'Luxury Car Dealer' },
  { value: 'yacht_dealer', label: 'Yacht / Marine' },
  { value: 'interior_designer', label: 'Interior Design / Concierge' },
  { value: 'other_business', label: 'Other Luxury Service' },
];

const PartnerLandingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const formRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // Region gate — `/partner` (bare) must always show the selector. Once
  // the visitor picks a region they land on `/uae/partner` (or `/in/partner`
  // → IndiaPartnerLandingPage which has its own branding).
  const path = location.pathname;
  const isScoped = path.startsWith('/in/') || path.startsWith('/india/') || path.startsWith('/uae/');
  const [form, setForm] = useState({
    contact_person: '', company_name: '', email: '', phone: '', business_type: '', country: '', city: '', website: '', description: ''
  });

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.contact_person || !form.company_name || !form.email || !form.phone || !form.business_type) {
      toast.error('Please fill all required fields');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        country_city: form.city ? `${form.city}, ${form.country}` : form.country,
      };
      delete payload.country;
      delete payload.city;
      await axios.post(`${API}/partner/apply/json`, payload);
      setSubmitted(true);
      toast.success('Application submitted! We will contact you shortly.');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Submission failed. Please try again.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const set = (key) => (e) => setForm(p => ({ ...p, [key]: typeof e === 'string' ? e : e.target.value }));

  // ── Region gate ─────────────────────────────────────────────────────
  // Bare `/partner` ALWAYS shows the selector. After picking, India
  // visitors go to `/in/partner` (which renders IndiaPartnerLandingPage)
  // and UAE visitors stay here at `/uae/partner` to view the UAE form.
  if (!isScoped) {
    return (
      <RegionSelector
        title="Become a Partner"
        subtitle="Select your region to continue"
        buildHref={(code) => (code === 'india' ? '/in/partner' : '/uae/partner')}
        bottomNote={
          <>Already a partner? <Link to="/partner/login" className="text-gold hover:underline">Sign in</Link></>
        }
        testIdPrefix="partner-apply-country"
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#060606] text-white" data-testid="partner-landing">
      {/* ── Navbar ── */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#060606]/80 backdrop-blur-xl border-b border-white/[0.04]">
        <div className="max-w-[1200px] mx-auto px-5 h-16 flex items-center justify-between gap-3">
          <Logo to="/" size="sm" wordmarkClassName="text-[#C6A15B]" tracking="tracking-[0.12em]" testId="partner-nav-logo" />
          <div className="hidden md:block"><PartnerCountrySwitch active="UAE" /></div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/partner/login')} className="text-xs text-white/40 hover:text-white transition-colors hidden sm:block">Partner Login</button>
            <button onClick={scrollToForm} className="bg-[#C6A15B] text-black text-xs font-semibold px-5 py-2.5 hover:bg-[#D4B06A] transition-colors" data-testid="nav-cta">
              Become a Partner
            </button>
          </div>
        </div>
        <div className="md:hidden border-t border-white/[0.04] px-5 py-2 flex justify-center">
          <PartnerCountrySwitch active="UAE" />
        </div>
      </nav>

      {/* ── 1. Hero ── */}
      <section className="relative min-h-[100vh] flex items-center justify-center pt-16" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/80 to-[#060606]" />
        </div>
        <div className="relative z-10 max-w-[800px] mx-auto px-6 text-center py-24">
          <Fade>
            <p className="text-[#C6A15B] text-[11px] tracking-[0.35em] uppercase mb-6 font-medium">Partner With ASTIIV BASGAT</p>
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light leading-[1.08] mb-6" style={serif}>
              Own the Buyer,<br />Not Just the Listing
            </h1>
            <p className="text-white/45 text-base sm:text-lg max-w-[560px] mx-auto mb-10 leading-relaxed">
              ASTIIV BASGAT is a luxury lifestyle acquisition platform. We bundle properties with cars, watches, and concierge services — delivering qualified, high-intent buyers directly to you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={scrollToForm} className="bg-[#C6A15B] text-black px-10 py-4 text-[13px] font-semibold tracking-[0.15em] uppercase hover:bg-[#D4B06A] transition-all flex items-center justify-center gap-2" data-testid="hero-cta-partner">
                Become a Partner <ArrowRight className="w-4 h-4" />
              </button>
              <button onClick={scrollToForm} className="border border-white/15 px-10 py-4 text-[13px] tracking-[0.15em] uppercase hover:bg-white/5 transition-all" data-testid="hero-cta-demo">
                Book a Demo
              </button>
            </div>
          </Fade>
        </div>
      </section>

      {/* ── 2. Problem ── */}
      <section className="py-24 sm:py-32 px-6">
        <div className="max-w-[900px] mx-auto text-center">
          <Fade>
            <p className="text-[#C6A15B] text-[11px] tracking-[0.3em] uppercase mb-4">The Problem</p>
            <h2 className="text-3xl sm:text-4xl font-light mb-12" style={serif}>Why Traditional Listings Fail</h2>
          </Fade>
          <div className="grid sm:grid-cols-3 gap-8">
            {[
              { title: 'Too Many Listings', desc: 'Portals are oversaturated. Your premium property drowns in a sea of average listings.' },
              { title: 'Overwhelmed Buyers', desc: 'High-net-worth buyers want curated experiences, not endless search results.' },
              { title: 'Zero Differentiation', desc: 'Traditional platforms treat a 50M villa the same as a studio apartment.' },
            ].map((item, i) => (
              <Fade key={item.title} delay={i * 0.1}>
                <div className="p-6 border border-white/[0.06] rounded-xl hover:border-[#C6A15B]/20 transition-all">
                  <h3 className="text-lg font-semibold mb-3" style={serif}>{item.title}</h3>
                  <p className="text-white/35 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </Fade>
            ))}
          </div>
        </div>
      </section>

      {/* ── 3. Solution (Bundle) ── */}
      <section className="py-24 sm:py-32 px-6 bg-[#0A0A0A]">
        <div className="max-w-[900px] mx-auto">
          <Fade>
            <div className="text-center mb-16">
              <p className="text-[#C6A15B] text-[11px] tracking-[0.3em] uppercase mb-4">The ASTIIV BASGAT Solution</p>
              <h2 className="text-3xl sm:text-4xl font-light mb-4" style={serif}>Lifestyle Bundles That Sell</h2>
              <p className="text-white/35 max-w-lg mx-auto">We don't just list properties — we create complete lifestyle packages that make buyers say yes.</p>
            </div>
          </Fade>
          <Fade delay={0.15}>
            <div className="border border-white/[0.06] rounded-2xl overflow-hidden bg-black/40">
              <div className="bg-gradient-to-r from-[#C6A15B]/10 to-transparent p-6 border-b border-white/[0.04]">
                <div className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#C6A15B]" /><span className="text-[10px] text-[#C6A15B] tracking-[0.2em] uppercase font-semibold">Example Bundle</span></div>
                <h3 className="text-xl font-light mt-1" style={serif}>Dubai Marina Dream</h3>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 divide-y lg:divide-y-0 lg:divide-x divide-white/[0.04]">
                {[
                  { icon: Building2, type: 'Property', name: 'Marina Penthouse', price: 'AED 15.2M', img: 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=600&q=80' },
                  { icon: Car,       type: 'Car',      name: 'Rolls-Royce Spectre', price: 'AED 1.8M',  img: 'https://images.unsplash.com/photo-1631295868223-63265b40d9e4?w=600&q=80' },
                  { icon: Gem,       type: 'Watch',    name: 'Patek Philippe',      price: 'AED 650K',  img: 'https://images.unsplash.com/photo-1622434641406-a158123450f9?w=600&q=80' },
                  { icon: Users,     type: 'Concierge',name: 'Butler + Chef',       price: 'AED 240K/yr', img: 'https://images.unsplash.com/photo-1556910103-1c02745aae4d?w=600&q=80' },
                ].map(item => (
                  <div key={item.type} className="text-center" data-testid={`bundle-${item.type.toLowerCase()}`}>
                    <div className="aspect-[4/3] overflow-hidden border-b border-white/[0.04] relative">
                      <img src={item.img} alt={item.name} className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" loading="lazy" />
                      <div className="absolute top-2 left-2 bg-black/60 backdrop-blur-sm px-2 py-1 rounded-full flex items-center gap-1.5">
                        <item.icon className="w-3 h-3 text-[#C6A15B]" />
                        <span className="text-[9px] text-white/80 uppercase tracking-wider font-medium">{item.type}</span>
                      </div>
                    </div>
                    <div className="p-4">
                      <p className="text-sm font-medium">{item.name}</p>
                      <p className="text-[#C6A15B] text-xs mt-1">{item.price}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Fade>
        </div>
      </section>

      {/* ── 4. Benefits ── */}
      <section className="py-24 sm:py-32 px-6">
        <div className="max-w-[1000px] mx-auto">
          <Fade><div className="text-center mb-16">
            <p className="text-[#C6A15B] text-[11px] tracking-[0.3em] uppercase mb-4">Why Partner With Us</p>
            <h2 className="text-3xl sm:text-4xl font-light" style={serif}>Built for Premium Partners</h2>
          </div></Fade>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Shield, title: 'Qualified Buyers', desc: 'Every lead is pre-qualified with verified budget and preferences.' },
              { icon: TrendingUp, title: 'Premium Positioning', desc: 'Your listings are bundled into aspirational lifestyle packages.' },
              { icon: Gem, title: 'Increased Deal Value', desc: 'Bundle economics mean higher per-transaction revenue for you.' },
              { icon: Check, title: 'Zero Risk Model', desc: 'Pay only for results. No upfront fees, no long-term contracts.' },
            ].map((b, i) => (
              <Fade key={b.title} delay={i * 0.08}>
                <div className="p-6 border border-white/[0.06] rounded-xl hover:border-[#C6A15B]/20 transition-all h-full">
                  <b.icon className="w-8 h-8 text-[#C6A15B] mb-4" />
                  <h3 className="text-base font-semibold mb-2">{b.title}</h3>
                  <p className="text-white/30 text-sm leading-relaxed">{b.desc}</p>
                </div>
              </Fade>
            ))}
          </div>
        </div>
      </section>

      {/* ── 5. Partner Types ── */}
      <section className="py-24 sm:py-32 px-6 bg-[#0A0A0A]">
        <div className="max-w-[900px] mx-auto text-center">
          <Fade>
            <p className="text-[#C6A15B] text-[11px] tracking-[0.3em] uppercase mb-4">Who We Work With</p>
            <h2 className="text-3xl sm:text-4xl font-light mb-12" style={serif}>Partner Types</h2>
          </Fade>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: Building2, label: 'Developers', sub: 'Dubai + India' },
              { icon: Briefcase, label: 'Brokerages', sub: 'Agencies & Agents' },
              { icon: Car, label: 'Luxury Cars', sub: 'Premium Dealers' },
              { icon: Users, label: 'Concierge', sub: 'Lifestyle Services' },
            ].map((p, i) => (
              <Fade key={p.label} delay={i * 0.08}>
                <div className="p-6 border border-white/[0.06] rounded-xl hover:border-[#C6A15B]/15 transition-all">
                  <p.icon className="w-8 h-8 text-[#C6A15B] mx-auto mb-3" />
                  <p className="font-semibold text-sm">{p.label}</p>
                  <p className="text-white/25 text-xs mt-1">{p.sub}</p>
                </div>
              </Fade>
            ))}
          </div>
        </div>
      </section>

      {/* ── 6. How It Works ── */}
      <section className="py-24 sm:py-32 px-6">
        <div className="max-w-[800px] mx-auto">
          <Fade><div className="text-center mb-16">
            <p className="text-[#C6A15B] text-[11px] tracking-[0.3em] uppercase mb-4">Simple Process</p>
            <h2 className="text-3xl sm:text-4xl font-light" style={serif}>How It Works</h2>
          </div></Fade>
          <div className="space-y-0">
            {[
              { n: '01', title: 'Submit Your Listings', desc: 'Add your properties, cars, or services to the ASTIIV BASGAT platform. We handle the rest.' },
              { n: '02', title: 'We Create Bundles', desc: 'Our AI pairs your listings with complementary luxury assets into irresistible lifestyle packages.' },
              { n: '03', title: 'Receive Qualified Leads', desc: 'High-intent buyers matched to your offerings. Pre-qualified, ready to transact.' },
            ].map((step, i) => (
              <Fade key={step.n} delay={i * 0.1}>
                <div className="flex gap-6 py-8 border-b border-white/[0.04] last:border-0">
                  <span className="text-[#C6A15B] text-3xl font-light opacity-40 shrink-0 w-12" style={serif}>{step.n}</span>
                  <div>
                    <h3 className="text-lg font-semibold mb-1">{step.title}</h3>
                    <p className="text-white/30 text-sm leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              </Fade>
            ))}
          </div>
        </div>
      </section>

      {/* ── 7. Global Buyers ── */}
      <section className="py-24 sm:py-32 px-6 bg-[#0A0A0A]">
        <div className="max-w-[800px] mx-auto text-center">
          <Fade>
            <Globe className="w-10 h-10 text-[#C6A15B] mx-auto mb-6" />
            <h2 className="text-3xl sm:text-4xl font-light mb-4" style={serif}>Global Buyer Network</h2>
            <p className="text-white/35 text-base max-w-lg mx-auto mb-10">
              ASTIIV BASGAT connects you with high-net-worth buyers from India, UAE, UK, and Europe — all actively seeking premium lifestyle investments.
            </p>
          </Fade>
          <Fade delay={0.1}>
            <div className="flex flex-wrap justify-center gap-3">
              {['India', 'Dubai (UAE)', 'United Kingdom', 'Europe', 'Singapore', 'Saudi Arabia'].map(r => (
                <span key={r} className="px-4 py-2 border border-white/[0.08] rounded-full text-xs text-white/40">{r}</span>
              ))}
            </div>
          </Fade>
        </div>
      </section>

      {/* ── 8. Positioning ── */}
      <section className="py-24 sm:py-32 px-6">
        <div className="max-w-[700px] mx-auto text-center">
          <Fade>
            <p className="text-[#C6A15B] text-base font-medium mb-6 tracking-wide">Not a listing platform.</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light leading-tight" style={serif}>
              A Luxury Lifestyle<br />Acquisition Platform
            </h2>
            <p className="text-white/30 text-base mt-6 max-w-lg mx-auto leading-relaxed">
              We combine property, automotive, lifestyle, and concierge into a single buying experience — positioning your brand alongside the world's most desirable assets.
            </p>
          </Fade>
        </div>
      </section>

      {/* ── 9. CTA ── */}
      <section className="py-24 sm:py-32 px-6 bg-[#0A0A0A]">
        <div className="max-w-[700px] mx-auto text-center">
          <Fade>
            <h2 className="text-3xl sm:text-4xl font-light mb-4" style={serif}>
              Let's Build the Future<br />of Luxury Buying
            </h2>
            <p className="text-white/30 mb-10 max-w-md mx-auto">Join ASTIIV BASGAT's exclusive partner network and access a new channel of qualified, high-intent luxury buyers.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={scrollToForm} className="bg-[#C6A15B] text-black px-10 py-4 text-[13px] font-semibold tracking-[0.15em] uppercase hover:bg-[#D4B06A] transition-all flex items-center justify-center gap-2">
                Become a Partner <ChevronRight className="w-4 h-4" />
              </button>
              <button onClick={scrollToForm} className="border border-white/15 px-10 py-4 text-[13px] tracking-[0.15em] uppercase hover:bg-white/5 transition-all">
                Schedule a Call
              </button>
            </div>
          </Fade>
        </div>
      </section>

      {/* ── 10. Lead Form ── */}
      <section ref={formRef} className="py-24 sm:py-32 px-6" id="partner-form" data-testid="partner-form-section">
        <div className="max-w-[560px] mx-auto">
          <Fade>
            <div className="text-center mb-10">
              <p className="text-[#C6A15B] text-[11px] tracking-[0.3em] uppercase mb-4">Get Started</p>
              <h2 className="text-3xl sm:text-4xl font-light mb-3" style={serif}>Request Partnership Access</h2>
              <p className="text-white/30 text-sm">Fill in your details and our team will reach out within 24 hours.</p>
            </div>
          </Fade>

          {submitted ? (
            <Fade>
              <div className="text-center p-12 border border-[#C6A15B]/30 rounded-2xl bg-[#C6A15B]/[0.03]" data-testid="form-success">
                <Check className="w-12 h-12 text-[#C6A15B] mx-auto mb-4" />
                <h3 className="text-2xl font-light mb-2" style={serif}>Application Received</h3>
                <p className="text-white/40 text-sm mb-6">Our partnership team will contact you within 24 hours.</p>
                <button onClick={() => navigate('/partner/login')} className="text-[#C6A15B] text-sm underline underline-offset-4 hover:text-[#D4B06A]">
                  Go to Partner Login
                </button>
              </div>
            </Fade>
          ) : (
            <Fade delay={0.1}>
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="partner-application-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label className="text-white/50 text-xs uppercase tracking-wider mb-2 block">Name *</Label>
                    <Input value={form.contact_person} onChange={set('contact_person')} placeholder="Full name" className="bg-white/[0.03] border-white/10 text-white placeholder-white/20 h-12" data-testid="form-name" required />
                  </div>
                  <div>
                    <Label className="text-white/50 text-xs uppercase tracking-wider mb-2 block">Company *</Label>
                    <Input value={form.company_name} onChange={set('company_name')} placeholder="Company name" className="bg-white/[0.03] border-white/10 text-white placeholder-white/20 h-12" data-testid="form-company" required />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label className="text-white/50 text-xs uppercase tracking-wider mb-2 block">Email *</Label>
                    <Input type="email" value={form.email} onChange={set('email')} placeholder="email@company.com" className="bg-white/[0.03] border-white/10 text-white placeholder-white/20 h-12" data-testid="form-email" required />
                  </div>
                  <div>
                    <Label className="text-white/50 text-xs uppercase tracking-wider mb-2 block">WhatsApp / Phone *</Label>
                    <Input value={form.phone} onChange={set('phone')} placeholder="+971 XX XXX XXXX" className="bg-white/[0.03] border-white/10 text-white placeholder-white/20 h-12" data-testid="form-phone" required />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label className="text-white/50 text-xs uppercase tracking-wider mb-2 block">Business Type *</Label>
                    <Select value={form.business_type} onValueChange={set('business_type')}>
                      <SelectTrigger className="bg-white/[0.03] border-white/10 text-white h-12" data-testid="form-business-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                      <SelectContent className="bg-[#1a1a1a] border-white/10 text-white">
                        {BUSINESS_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-white/50 text-xs uppercase tracking-wider mb-2 block">Country *</Label>
                    <Select value={form.country} onValueChange={v => setForm(p => ({ ...p, country: v, city: '' }))}>
                      <SelectTrigger className="bg-white/[0.03] border-white/10 text-white h-12" data-testid="form-country"><SelectValue placeholder="Select country" /></SelectTrigger>
                      <SelectContent className="bg-[#1a1a1a] border-white/10 text-white">
                        {Object.keys(COUNTRY_CITIES).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-white/50 text-xs uppercase tracking-wider mb-2 block">City</Label>
                    {form.country && COUNTRY_CITIES[form.country]?.length > 0 ? (
                      <Select value={form.city} onValueChange={v => setForm(p => ({ ...p, city: v }))}>
                        <SelectTrigger className="bg-white/[0.03] border-white/10 text-white h-12" data-testid="form-city"><SelectValue placeholder="Select city" /></SelectTrigger>
                        <SelectContent className="bg-[#1a1a1a] border-white/10 text-white">
                          {(COUNTRY_CITIES[form.country] || []).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input value={form.city} onChange={set('city')} placeholder="Enter city" className="bg-white/[0.03] border-white/10 text-white placeholder-white/20 h-12" data-testid="form-city" />
                    )}
                  </div>
                </div>
                <div>
                  <Label className="text-white/50 text-xs uppercase tracking-wider mb-2 block">Website</Label>
                  <Input value={form.website} onChange={set('website')} placeholder="https://yourcompany.com" className="bg-white/[0.03] border-white/10 text-white placeholder-white/20 h-12" data-testid="form-website" />
                </div>
                <Button type="submit" disabled={submitting} className="w-full bg-[#C6A15B] text-black hover:bg-[#D4B06A] h-14 text-[13px] font-semibold tracking-[0.15em] uppercase" data-testid="form-submit-btn">
                  {submitting ? 'Submitting...' : 'Request Partnership Access'}
                </Button>
                <p className="text-center text-white/15 text-xs mt-2">By submitting, you agree to our terms. No upfront fees.</p>
              </form>
            </Fade>
          )}
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="py-10 px-6 border-t border-white/[0.04]">
        <div className="max-w-[1200px] mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <Logo to={null} size="xs" wordmarkClassName="text-[#C6A15B]" tracking="tracking-[0.12em]" testId="partner-footer-logo" />
          <div className="flex items-center gap-6">
            <a href="mailto:partners@dreamlife.ae" className="flex items-center gap-2 text-white/25 text-xs hover:text-white/50 transition-colors"><Mail className="w-3 h-3" /> partners@dreamlife.ae</a>
            <a href="tel:+971000000000" className="flex items-center gap-2 text-white/25 text-xs hover:text-white/50 transition-colors"><Phone className="w-3 h-3" /> +971 XXX XXXX</a>
          </div>
          <p className="text-white/10 text-[10px]">&copy; 2026 ASTIIV BASGAT. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default PartnerLandingPage;
